import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Avatar,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { getTicketThreadDetail } from "app/services/schoolReportingServices/zohoDesk.service";

const getName = (msg) => msg.author?.name || "Agent";
const getInitials = (msg) => {
  const name = getName(msg);
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};
const getContent = (msg) => msg.summary || msg.content || "";
const getTime = (msg) =>
  msg.createdTime ? format(parseISO(msg.createdTime), "hh:mm a") : "";
const getRelativeTime = (msg) =>
  msg.createdTime
    ? formatDistanceToNow(parseISO(msg.createdTime), { addSuffix: true })
    : "";

const sortOptions = [
  { value: "asc", label: "Oldest First" },
  { value: "desc", label: "Newest First" },
];

function ReplyItem({ msg, idx, isExpanded, isLoading, fullContent, onExpand }) {
  return (
    <Paper
      key={msg.id || idx}
      elevation={1}
      sx={{
        p: 2,
        width: "100%",
        bgcolor: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        boxShadow: 0,
        mb: 0.5,
        position: "relative",
        cursor: "pointer",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 2 },
      }}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={() => onExpand(idx, msg)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onExpand(idx, msg);
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Avatar
          sx={{
            bgcolor: "#e3f2fd",
            color: "#1976d2",
            fontWeight: 700,
          }}
        >
          {getInitials(msg)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
            >
              {getName(msg)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getTime(msg)}
            </Typography>
            <Typography variant="caption" color="text.disabled ">
              ( {getRelativeTime(msg)} )
            </Typography>
            {msg.respondedIn && (
              <Typography variant="caption" color="success.main">
                ( responded in {msg.respondedIn} )
              </Typography>
            )}
          </Stack>
          <Box
            mt={1}
            mb={1}
            sx={{
              overflow: "visible",
              maxHeight: "none",
              height: "auto",
              minHeight: 0,
            }}
          >
            {isExpanded ? (
              isLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading full content...
                </Typography>
              ) : (
                <Box
                  sx={{
                    wordBreak: "break-word",
                    overflow: "visible",
                    maxHeight: "none",
                    height: "auto",
                    minHeight: 0,
                  }}
                >
                  {fullContent ? (
                    <div
                      style={{
                        fontSize: 15,
                        fontFamily: "inherit",
                        overflow: "visible",
                        maxHeight: "none",
                        height: "auto",
                        minHeight: 0,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: fullContent,
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No additional content.
                    </Typography>
                  )}
                </Box>
              )
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                {getContent(msg)}
              </Typography>
            )}
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Typography
                variant="subtitle1"
                color="primary"
                sx={{ fontWeight: 500, fontSize: 11 }}
              >
                {isExpanded ? "Show less" : "Show more"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Tooltip title="Reply by Email">
            <IconButton size="small">
              <MailOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );
}

ReplyItem.propTypes = {
  msg: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  fullContent: PropTypes.string,
  onExpand: PropTypes.func.isRequired,
};

const TicketReplyPreview = ({ conversations, ticketId }) => {
  const [sortOrder, setSortOrder] = useState("asc");
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [loadingIdx, setLoadingIdx] = useState(null);
  const [fullThreads, setFullThreads] = useState({});

  const handleSortChange = (event, newOrder) => {
    if (newOrder) setSortOrder(newOrder);
  };

  const handleExpand = async (idx, msg) => {
    if (expandedIdx === idx) {
      setExpandedIdx(null);
      return;
    }
    if (fullThreads[msg.id]) {
      setExpandedIdx(idx);
      return;
    }
    setLoadingIdx(idx);
    try {
      const res = await getTicketThreadDetail(ticketId, msg.id);
      setFullThreads((prev) => ({
        ...prev,
        [msg.id]: res.data?.thread?.content || msg.summary || msg.content || "",
      }));
      setExpandedIdx(idx);
    } catch (e) {
      setFullThreads((prev) => ({
        ...prev,
        [msg.id]: "Failed to load full content.",
      }));
      setExpandedIdx(idx);
    } finally {
      setLoadingIdx(null);
    }
  };

  const replies = conversations.filter(
    (msg) => msg.author?.type !== "END_USER"
  );
  const sortedReplies = [...replies].sort((a, b) => {
    if (sortOrder === "asc") {
      return new Date(a.createdTime) - new Date(b.createdTime);
    } else {
      return new Date(b.createdTime) - new Date(a.createdTime);
    }
  });

  return (
    <Box sx={{ width: "100%", my: 4 }}>
      <ToggleButtonGroup
        value={sortOrder}
        exclusive
        onChange={handleSortChange}
        sx={{ mb: 2 }}
        size="small"
      >
        {sortOptions.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack spacing={3} sx={{ width: "100%" }}>
        {sortedReplies.length === 0 ? (
          <Typography color="text.secondary" align="center">
            No agent replies yet.
          </Typography>
        ) : (
          sortedReplies.map((msg, idx) => (
            <ReplyItem
              key={msg.id || idx}
              msg={msg}
              idx={idx}
              isExpanded={expandedIdx === idx}
              isLoading={loadingIdx === idx}
              fullContent={fullThreads[msg.id]}
              onExpand={handleExpand}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

TicketReplyPreview.propTypes = {
  conversations: PropTypes.array.isRequired,
  ticketId: PropTypes.string.isRequired,
};

export default TicketReplyPreview;
